import { useState } from "react";
import { useNavigate } from "react-router";
import PropTypes from "prop-types";
import {
  ActionMenu,
  MenuSection,
  MenuItem,
  Text,
  DialogContainer,
  AlertDialog,
  TextField,
} from "@react-spectrum/s2";
import RenameIcon from "@react-spectrum/s2/icons/Rename";
import DuplicateIcon from "@react-spectrum/s2/icons/Duplicate";
import UserAddIcon from "@react-spectrum/s2/icons/UserAdd";
import DeleteIcon from "@react-spectrum/s2/icons/Delete";
import {
  useDeleteVideo,
  useUpdateVideo,
  useDuplicateVideo,
} from "@app/hooks/videos";
import { ShareVideoDialog } from "./ShareVideoDialog";
import { store } from "../../store";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

export const ProjectMenu = ({ size = "M", item }) => {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null);
  const [tempName, setTempName] = useState("");
  const updateVideo = useUpdateVideo(item?.id);
  const deleteVideo = useDeleteVideo(item?.id);
  const duplicateVideo = useDuplicateVideo(item?.id);

  const handleShare = async (emails) => {
    try {
      const current = item.sharedWith ?? [];
      const currentLower = new Set(current.map((email) => email.toLowerCase()));
      const added = emails.filter(
        (email) => !currentLower.has(email.toLowerCase()),
      );

      if (added.length === 0) {
        store.getState().addToast("Project is already shared with these users", {
          variant: "info",
          timeout: 3000,
        });
        return;
      }

      await updateVideo.mutateAsync({
        id: item.id,
        sharedWith: [...current, ...added],
      });

      store.getState().addToast(`Project shared with ${added.join(", ")}`, {
        variant: "positive",
        timeout: 3000,
      });
    } catch (error) {
      store.getState().addToast("Unable to share project. Please try again.", {
        variant: "negative",
        timeout: 5000,
      });
      throw error;
    }
  };

  const handleAction = (key) => {
    if (!item?.id) return;

    switch (key) {
      case "share":
        setDialog("share");
        break;
      case "rename":
        setTempName(item.name ?? "");
        setDialog("rename");
        break;
      case "duplicate":
        duplicateVideo.mutate(
          { id: item.id },
          {
            onSuccess: () => {
              store.getState().addToast("Project duplicated successfully", {
                variant: "positive",
                timeout: 3000,
              });
              navigate("/projects");
            },
          },
        );
        break;
      case "delete":
        deleteVideo.mutate(
          { id: item.id },
          {
            onSuccess: () => {
              store.getState().addToast("Project deleted successfully", {
                variant: "positive",
                timeout: 3000,
              });
              navigate("/projects");
            },
          },
        );
        break;
      default:
        break;
    }
  };

  return (
    <>
      <ActionMenu
        isQuiet
        isDisabled={!item?.id || deleteVideo.isPending}
        size={size}
        aria-label="Project actions"
        styles={style({ marginStart: 4 })}
        onAction={handleAction}
        disabledKeys={duplicateVideo.isPending ? ["duplicate"] : []}
      >
        <MenuSection>
          <MenuItem id="share">
            <UserAddIcon />
            <Text>Share</Text>
          </MenuItem>
        </MenuSection>
        <MenuSection>
          <MenuItem id="rename">
            <RenameIcon />
            <Text>Rename</Text>
          </MenuItem>
          <MenuItem id="duplicate">
            <DuplicateIcon />
            <Text>
              {duplicateVideo.isPending ? "Duplicating..." : "Duplicate"}
            </Text>
          </MenuItem>
        </MenuSection>
        <MenuSection>
          <MenuItem id="delete">
            <DeleteIcon />
            <Text>Delete</Text>
          </MenuItem>
        </MenuSection>
      </ActionMenu>
      <DialogContainer onDismiss={() => setDialog(null)}>
        {dialog === "share" && (
          <ShareVideoDialog videoId={item.id} onAccept={handleShare} />
        )}
        {dialog === "rename" && (
          <AlertDialog
            title="Rename Project"
            primaryActionLabel="Rename"
            cancelLabel="Cancel"
            onPrimaryAction={() => {
              updateVideo.mutate({ id: item.id, name: tempName });
              setTempName("");
            }}
          >
            <TextField label="Title" value={tempName} onChange={setTempName} />
          </AlertDialog>
        )}
      </DialogContainer>
    </>
  );
};

ProjectMenu.propTypes = {
  size: PropTypes.oneOf(["S", "M", "L"]),
  item: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    sharedWith: PropTypes.arrayOf(PropTypes.string),
  }),
};
